import React, { useEffect, useState } from 'react';
import {
	Css,
	Row,
	StyledCheckbox,
	StyledTextField,
	TextButton,
} from '../../../../../../ui';
import { useTranslation } from '../../../../../shared/i18n/translate/useTranslation';
import { DetailsFormSection as Section } from '../DetailsFormSection';
import { LicensePlateNumber } from '../../../../../auth/shared/form-fields/LicensePlateNumber';
import { ConfirmLicensePlateNumber } from '../../../../../auth/shared/form-fields/ConfirmLicensePlateNumber';
import {
	VehiclesList,
	VehicleTextButton,
} from '../../../../../account/vehicles/ui/VehiclesList';
import { useVehicleStores } from '../../../../../account/vehicles/domain/stores/useVehicleStores';
import { useLoadMyVehicles } from '../../../../../account/vehicles/domain/use-cases/useLoadMyVehicles';
import { observer } from 'mobx-react';
import { StyledCardContainer } from '../../../../../account/vehicles/ui/VehiclesCardContainer';
import styled from '@emotion/styled';
import { Color } from '../../../../../shared/styles/Color';
import { SaveVehicle } from '../../../../../auth/shared/form-fields/SaveVehicle';
import { IVehicle } from '../../../../../shared/api/gateway/models/Vehicle';
import { VehiclesShimmer } from '../../../../../account/vehicles/ui/VehiclesShimmer';
import { useAuthStore } from '../../../../../auth/shared/store/useAuthStore';
import { useFormContext } from 'react-hook-form';
import { theme } from '../../../../../shared/styles';
import Responsive from '../../../../../../ui/utils/Responsive';

export interface IVehicleDetailsFormData {
	licensePlateNumber: string;
	confirmLicensePlateNumber: string;
	saveVehicle: boolean;
	usedExistingVehicleFromAccount: boolean;
}

interface VehicleDetailsProps {
	modifyBooking?: boolean;
	bookingLicensePlate?: string;
}

export const VehicleDetails = observer(
	({ modifyBooking, bookingLicensePlate }: VehicleDetailsProps) => {
		const { t } = useTranslation();
		const [selectedVehicle, setSelectedVehicle] = useState<IVehicle | null>(
			null,
		);

		const { isAuthenticated } = useAuthStore();
		const { getVehicleStore } = useVehicleStores();
		const { setValue } = useFormContext();

		const { value, isLoading: isLoadingVehicles } = getVehicleStore;
		const vehicles = value || [];
		const noVehiclesInAccount = vehicles.length === 0;

		const initialFavoriteVehicle =
			vehicles.find((vehicle) => vehicle.favorite) || null;

		const vehicleFromBooking = vehicles.find(
			(vehicle) => vehicle.licensePlate === bookingLicensePlate,
		);

		const [showVehicleLicensePlateInputs, setShowVehicleLicensePlateInputs] =
			useState(false);

		useEffect(() => {
			if (modifyBooking) return;

			if (initialFavoriteVehicle) {
				setSelectedVehicle(initialFavoriteVehicle);
			}
		}, [vehicles, modifyBooking]);

		useEffect(() => {
			if (!selectedVehicle || modifyBooking) return;

			const { licensePlate } = selectedVehicle;
			setValue('licensePlateNumber', licensePlate);
			setValue('confirmLicensePlateNumber', licensePlate);
		}, [selectedVehicle, modifyBooking]);

		useEffect(() => {
			setValue('usedExistingVehicleFromAccount', !!selectedVehicle);
		}, [selectedVehicle, setValue]);

		useEffect(() => {
			if (!modifyBooking) return;

			if (bookingLicensePlate) {
				if (vehicleFromBooking) {
					setSelectedVehicle(vehicleFromBooking);
					setShowVehicleLicensePlateInputs(false);
				} else {
					setSelectedVehicle(null);
					setShowVehicleLicensePlateInputs(true);

					setValue('licensePlateNumber', bookingLicensePlate);
					setValue('confirmLicensePlateNumber', bookingLicensePlate);
				}
			}
		}, [vehicles, modifyBooking, bookingLicensePlate]);

		const onShowVehicleLicensePlateInputs = () => {
			setShowVehicleLicensePlateInputs(true);
			setSelectedVehicle(null);

			setValue('licensePlateNumber', '');
			setValue('confirmLicensePlateNumber', '');
		};

		const handleSelectVehicle = (vehicle: IVehicle) => {
			setSelectedVehicle(vehicle);

			setValue('licensePlateNumber', vehicle.licensePlate);
			setValue('confirmLicensePlateNumber', vehicle.licensePlate);

			setShowVehicleLicensePlateInputs(false);
		};

		useLoadMyVehicles();

		const requiredHint = t('user_sign_up_custom_details_hint');

		const licensePlateInputs = (
			<LicensePlateInputWrapper>
				<LicensePlateNumber
					required
					hint={requiredHint}
					defaultValue={
						initialFavoriteVehicle?.licensePlate ||
						selectedVehicle?.licensePlate
					}
				/>
				<ConfirmLicensePlateNumber
					required
					hint={requiredHint}
					defaultValue={
						initialFavoriteVehicle?.licensePlate ||
						selectedVehicle?.licensePlate
					}
				/>
				{isAuthenticated && (
					<StyledLicensePlateRequest>
						{t('add_vehicle_form_license_plate_support_text')}
					</StyledLicensePlateRequest>
				)}
			</LicensePlateInputWrapper>
		);

		return (
			<Section title={t('web_parking_details_vehicle_details_header')}>
				{isAuthenticated && (
					<>
						{isLoadingVehicles ? (
							<VehiclesShimmer />
						) : (
							<StyledCardContainer>
								<VehiclesList
									vehiclesList={vehicles}
									selectedVehicle={selectedVehicle}
									setSelectedVehicle={handleSelectVehicle}
									isBookingDetailPage
								/>
							</StyledCardContainer>
						)}

						{vehicles.length > 0 && (
							<>
								<StyledDivider>
									{t('parking_personal_details_vehicle_details_divider')}
								</StyledDivider>
								<OtherVehicleButtonWrap
									isVisible={!showVehicleLicensePlateInputs}
								>
									{!showVehicleLicensePlateInputs && (
										<StyledVehicleTextButton
											type="button"
											onClick={onShowVehicleLicensePlateInputs}
										>
											<span>
												{t(
													'parking_personal_details_vehicle_details_use_other_vehicle',
												)}
											</span>
										</StyledVehicleTextButton>
									)}
								</OtherVehicleButtonWrap>
							</>
						)}
					</>
				)}
				<AnimatedInputContainer
					isVisible={
						showVehicleLicensePlateInputs ||
						!isAuthenticated ||
						noVehiclesInAccount
					}
				>
					<>
						{licensePlateInputs}
						{isAuthenticated && (
							<StyledRow columns={1}>
								<SaveVehicle />
							</StyledRow>
						)}
					</>
				</AnimatedInputContainer>
			</Section>
		);
	},
);

// Horizontal Rule with centered text in middle
const StyledDivider = styled.div`
	display: flex;
	align-items: center;
	text-align: center;

	font-size: ${Css.pxToRem(21)};
	font-weight: 600;
	margin: 24px 0;
	color: ${Color.SurfaceLight};

	&::before,
	&::after {
		content: '';
		flex: 1;
		border-bottom: 1.5px solid ${Color.Neutral90};
	}

	&:not(:empty)::before {
		margin-right: 10px;
	}

	&:not(:empty)::after {
		margin-left: 10px;
	}
`;

const OtherVehicleButtonWrap = styled.div<{ isVisible: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;

	opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
	transition: opacity 0.3s ease;

	${TextButton} {
		color: ${Color.PrimaryDefault};
		font-weight: 600;
	}
`;

const AnimatedInputContainer = styled.div<{ isVisible: boolean }>`
	overflow: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
	max-height: ${({ isVisible }) => (isVisible ? '450px' : '0')};
	opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
	transition: max-height 0.3s ease, opacity 0.3s ease;
`;

const StyledVehicleTextButton = styled(VehicleTextButton)`
	color: ${Color.PrimaryDefault};
	font-size: ${Css.pxToRem(18)};
	font-weight: 600;
	height: 0;
	margin-bottom: 32px;

	&:focus-visible {
		outline: ${theme.button?.primary?.focus?.outline};
		border-radius: 5px;
	}
`;

const LicensePlateInputWrapper = styled(Row)`
	flex-direction: column;

	& > div {
		width: 50% !important;

		@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
			width: auto !important;
		}
	}
`;

export const StyledLicensePlateRequest = styled.p`
	line-height: normal;
	color: ${Color.Surface2};
	font-size: ${Css.pxToRem(16)};
	margin-top: -12px;
	margin-left: 3px;
`;

const StyledRow = styled(Row)`
	${StyledTextField}, ${StyledCheckbox} {
		margin: 3px;
	}
`;
